/**
 * RevenueCat SDK integration for subscription management
 * 
 * This module handles:
 * - Initializing RevenueCat SDK
 * - Purchasing subscriptions
 * - Checking subscription status
 * - Restoring purchases
 * 
 * Note: RevenueCat SDK is loaded dynamically to handle module import issues
 */

const REVENUECAT_API_KEY = process.env.REACT_APP_REVENUECAT_API_KEY;
let PurchasesSDK = null;
let SDKLoadingPromise = null;
let isInitialized = false;
let purchasesInstance = null; // Store the Purchases instance returned from configure()

// Generate or retrieve anonymous user ID from sessionStorage
const getOrCreateAnonymousUserId = () => {
  try {
    // Check if sessionStorage is available (not available in SSR)
    if (typeof window === 'undefined' || !window.sessionStorage) {
      // Generate ID without storing (for SSR or when sessionStorage unavailable)
      return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    let anonymousUserId = sessionStorage.getItem('rc_anonymous_user_id');
    if (!anonymousUserId || anonymousUserId.trim() === '') {
      // Generate a random anonymous ID
      anonymousUserId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      try {
        sessionStorage.setItem('rc_anonymous_user_id', anonymousUserId);
      } catch (e) {
        // If storage fails, just return the generated ID
        console.warn('Failed to store anonymous user ID:', e);
      }
    }
    
    // Ensure we have a valid ID
    if (!anonymousUserId || anonymousUserId.trim() === '') {
      anonymousUserId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    return anonymousUserId;
  } catch (error) {
    // Fallback: always return a valid ID
    console.error('Error in getOrCreateAnonymousUserId:', error);
    return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

/**
 * Load RevenueCat SDK dynamically
 * @returns {Promise<Object>} Purchases SDK object
 */
const loadPurchasesSDK = async () => {
  if (PurchasesSDK) return PurchasesSDK;
  if (SDKLoadingPromise) return SDKLoadingPromise;
  
  SDKLoadingPromise = (async () => {
    try {
      const module = await import('@revenuecat/purchases-js');
      PurchasesSDK = module.default || module.Purchases || module;
      return PurchasesSDK;
    } catch (error) {
      console.error('Failed to load RevenueCat SDK:', error);
      SDKLoadingPromise = null;
      return null;
    }
  })();
  
  return SDKLoadingPromise;
};

/**
 * Check if RevenueCat is configured
 * 
 * @returns {boolean} True if RevenueCat is properly configured
 */
export const isRevenueCatConfigured = () => {
  if (!REVENUECAT_API_KEY) {
    return false;
  }
  return true;
};

/**
 * Get Purchases SDK class (loads if necessary)
 * @returns {Promise<Object>} Purchases SDK class
 */
const getPurchases = async () => {
  if (!PurchasesSDK) {
    await loadPurchasesSDK();
  }
  return PurchasesSDK;
};

/**
 * Get the Purchases instance (the configured instance, not the class)
 * @returns {Promise<Object|null>} Purchases instance or null if not initialized
 */
const getPurchasesInstance = async () => {
  // First try to use the stored instance from configure()
  if (purchasesInstance) {
    return purchasesInstance;
  }
  
  // If not available, try to get the shared instance
  try {
    const Purchases = await getPurchases();
    if (!Purchases) return null;
    
    if (Purchases.isConfigured()) {
      purchasesInstance = Purchases.getSharedInstance();
      return purchasesInstance;
    }
  } catch (error) {
    console.warn('Could not get Purchases shared instance:', error);
  }
  
  return null;
};

/**
 * Initialize RevenueCat SDK
 * 
 * @param {string} userId - Firebase user UID (optional, can be set later)
 * @returns {Promise<boolean>} - True if initialized successfully
 */
export const initRevenueCat = async (userId = null) => {
  if (!isRevenueCatConfigured()) {
    console.warn('RevenueCat not configured. Please set REACT_APP_REVENUECAT_API_KEY');
    return false;
  }

  // Only initialize once
  if (isInitialized && purchasesInstance) {
    // If user ID provided and different, identify user
    if (userId) {
      await identifyUser(userId);
    }
    return true;
  }

  try {
    const Purchases = await getPurchases();
    if (!Purchases) {
      console.warn('RevenueCat SDK failed to load');
      return false;
    }

    // For Web Billing, always provide a user ID (generate anonymous if needed)
    let appUserID;
    if (userId) {
      appUserID = userId;
    } else {
      appUserID = getOrCreateAnonymousUserId();
    }
    
    // Validate appUserID is not empty/undefined
    if (!appUserID || appUserID.trim() === '') {
      console.error('Failed to generate valid user ID');
      return false;
    }
    
    console.log('Initializing RevenueCat with user ID:', appUserID);
    console.log('User ID type:', typeof appUserID);
    console.log('User ID length:', appUserID?.length);
    console.log('API Key:', REVENUECAT_API_KEY ? `${REVENUECAT_API_KEY.substring(0, 10)}...` : 'NOT SET');
    
    // Use appUserId (lowercase 'd') - this is the correct property name per SDK TypeScript definitions
    const config = {
      apiKey: REVENUECAT_API_KEY,
      appUserId: appUserID,  // Property name is appUserId (lowercase 'd'), value is our appUserID variable
    };
    
    console.log('Config object:', JSON.stringify(config, null, 2));

    // Call configure with appUserId - it returns a Purchases instance
    purchasesInstance = Purchases.configure(config);
    isInitialized = true;

    console.log('RevenueCat initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing RevenueCat:', error);
    return false;
  }
};

/**
 * Get current customer info (subscription status)
 * 
 * @returns {Promise<Object>} Customer info with entitlements
 */
export const getCustomerInfo = async () => {
  if (!isRevenueCatConfigured()) {
    return null;
  }

  try {
    const purchases = await getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized. Please call initRevenueCat() first.');
      return null;
    }
    
    const customerInfo = await purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('Error getting customer info:', error);
    return null;
  }
};

/**
 * Check if user has Pro subscription
 * 
 * @returns {Promise<boolean>} True if user has active Pro subscription
 */
export const isProSubscriber = async () => {
  if (!isRevenueCatConfigured()) {
    return false;
  }

  try {
    const purchases = await getPurchasesInstance();
    if (!purchases) return false;
    
    const customerInfo = await purchases.getCustomerInfo();
    return customerInfo.entitlements.active['pro'] !== undefined;
  } catch (error) {
    console.error('Error checking Pro subscription:', error);
    return false;
  }
};

/**
 * Get available offerings (products available for purchase)
 * 
 * @returns {Promise<Object>} Offerings object with available packages
 */
export const getOfferings = async () => {
  if (!isRevenueCatConfigured()) {
    return null;
  }

  try {
    const purchases = await getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized. Please call initRevenueCat() first.');
      return null;
    }
    
    // First, try to get offerings (will return current offering if set)
    let offerings = await purchases.getOfferings();
    
    // If no current offering, try fetching by identifier "default" as fallback
    if (!offerings || !offerings.current) {
      console.log('No current offering found, trying to fetch "default" offering by identifier...');
      offerings = await purchases.getOfferings({ offeringIdentifier: 'default' });
    }
    
    return offerings;
  } catch (error) {
    console.error('Error getting offerings:', error);
    return null;
  }
};

/**
 * Purchase a subscription package
 * 
 * @param {string} packageId - Package identifier (e.g., 'pro_monthly', 'pro_annual')
 * @returns {Promise<Object>} Purchase result with customer info
 */
export const purchasePackage = async (packageId) => {
  if (!isRevenueCatConfigured()) {
    throw new Error('RevenueCat not configured');
  }

  try {
    const purchases = await getPurchasesInstance();
    if (!purchases) {
      throw new Error('RevenueCat not initialized. Please call initRevenueCat() first.');
    }

    const offerings = await purchases.getOfferings();
    const offering = offerings.current;

    if (!offering) {
      throw new Error('No offering available');
    }

    // Find the package by identifier
    const pkg = offering.availablePackages.find(p => p.identifier === packageId);
    
    if (!pkg) {
      throw new Error(`Package ${packageId} not found`);
    }

    const { customerInfo } = await purchases.purchasePackage(pkg);
    
    return {
      success: true,
      customerInfo,
      isPro: customerInfo.entitlements.active['pro'] !== undefined,
    };
  } catch (error) {
    // Handle user cancellation
    if (error.userCancelled) {
      return {
        success: false,
        cancelled: true,
        error: 'Purchase cancelled',
      };
    }

    // Handle other errors
    console.error('Purchase error:', error);
    throw error;
  }
};

/**
 * Restore purchases (for users who subscribed elsewhere)
 * 
 * @returns {Promise<Object>} Restore result
 */
export const restorePurchases = async () => {
  if (!isRevenueCatConfigured()) {
    throw new Error('RevenueCat not configured');
  }

  try {
    const purchases = await getPurchasesInstance();
    if (!purchases) {
      throw new Error('RevenueCat not initialized. Please call initRevenueCat() first.');
    }
    
    const customerInfo = await purchases.restorePurchases();
    
    return {
      success: true,
      customerInfo,
      isPro: customerInfo.entitlements.active['pro'] !== undefined,
    };
  } catch (error) {
    console.error('Error restoring purchases:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Identify user in RevenueCat (Web Billing uses identifyUser/reconfigure, not logIn)
 * 
 * @param {string} userId - Firebase user UID
 * @returns {Promise<boolean>} True if identified successfully
 */
export const identifyUser = async (userId) => {
  if (!isRevenueCatConfigured() || !userId || userId.trim() === '') {
    console.warn('Cannot identify user: invalid userId');
    return false;
  }

  try {
    // For Web Billing, reconfigure with user ID
    // Note: Web SDK doesn't have logIn/identifyUser like mobile SDK
    // We need to reconfigure with the new user ID
    const Purchases = await getPurchases();
    if (!Purchases) return false;
    
    purchasesInstance = Purchases.configure({
      apiKey: REVENUECAT_API_KEY,
      appUserId: userId,  // Property name is appUserId (lowercase 'd')
    });
    
    // Update initialization state
    isInitialized = true;
    
    console.log('User identified in RevenueCat:', userId);
    return true;
  } catch (error) {
    console.error('Error identifying user in RevenueCat:', error);
    return false;
  }
};

/**
 * Log in user to RevenueCat (alias for identifyUser for Web Billing compatibility)
 * 
 * @param {string} userId - Firebase user UID
 * @returns {Promise<boolean>} True if logged in successfully
 */
export const logInUser = async (userId) => {
  return identifyUser(userId);
};

/**
 * Log out user from RevenueCat
 * 
 * @returns {Promise<boolean>} True if logged out successfully
 */
export const logOutUser = async () => {
  if (!isRevenueCatConfigured()) {
    return false;
  }

  try {
    // For Web Billing, reconfigure with anonymous user ID (reset to anonymous)
    const Purchases = await getPurchases();
    if (!Purchases) return false;
    
    const anonymousUserId = getOrCreateAnonymousUserId();
    purchasesInstance = Purchases.configure({
      apiKey: REVENUECAT_API_KEY,
      appUserId: anonymousUserId,
    });
    
    console.log('User logged out from RevenueCat (reset to anonymous)');
    return true;
  } catch (error) {
    console.error('Error logging out user from RevenueCat:', error);
    return false;
  }
};
